import React, { useState } from 'react';
import {
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    MenuItem,
    TextField,
} from "@mui/material";
import { Dialog } from "@mui/material";
import { UserItemType, UserLocationType } from "../../../model/User";
import {
    AddToCartItem,
    CartItemIdentifier,
    useAddToCartMutation,
} from "../../../services/grace";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAlertDispatch } from '../../../context/AlertContext/AlertContext';

export interface CartItemDialogValue {
    serviceType: string;
    location: string;
    quantity: number;
    requestor: string;
    costCentre: string;
    comment: string;
}

declare type CartItemDialogType = 'item' | 'material' | 'service';
declare type CartItemDialogMode = 'add';

interface DialogParams {
    label: string;
    title: string;
    showQuantity: boolean;
    showState: boolean;
}

function getDialogParams(cartItems: Array<CartItemIdentifier>, type: CartItemDialogType): DialogParams {
    switch (type) {
        case 'material':
            return {
                label: 'Material',
                title: 'Add material to cart',
                showQuantity: true,
                showState: true,
            };
        case 'service':
            return {
                label: 'Service',
                title: 'Add service to cart',
                showQuantity: true,
                showState: true,
            };
        default: {
            const quantity = cartItems.length === 1 ? "item" : "items";
            return {
                label: 'Service type',
                title: `Add ${quantity} to cart`,
                showQuantity: false,
                showState: false,
            };
        }
    }
}

export interface CartItemDialogProps {
    open: boolean;
    type: CartItemDialogType;
    mode: CartItemDialogMode;
    cartItems: Array<CartItemIdentifier>;
    locationTypes?: Array<UserLocationType>;
    itemTypes?: Array<UserItemType>;
    value?: CartItemDialogValue;
    onClose: () => void;
    onSubmit: () => void;
}

export default function CartItemDialog(props: CartItemDialogProps): JSX.Element {
    const {
        onClose,
        onSubmit,
        type,
        cartItems,
        itemTypes,
        value,
        locationTypes,
        ...other
    } = props;

    const alertDispatch = useAlertDispatch();

    const [addToCart] = useAddToCartMutation();
    const [inProgress, setInProgress] = useState(false);

    const { label, title, showQuantity, showState } = getDialogParams(cartItems, type);

    const initialValue = value !== undefined ? value : {
        serviceType: "",
        location: "",
        quantity: 1,
        requestor: "",
        costCentre: "",
        comment: "",
    };

    const [formValues, setFormValues] = useState<CartItemDialogValue>(initialValue);

    const handleClose = () => {
        if (!inProgress) {
            onClose();
        }
    };

    const handleSubmit = async () => {
        if (formValues.serviceType === "") {
            return;
        }

        if (showState && formValues.location === "") {
            return;
        }

        if (showQuantity && formValues.quantity < 1) {
            return;
        }

        const payload: AddToCartItem = {
            items: [],
            serviceCode: "",
        }

        if (type === "item") {
            payload.items = cartItems;
            payload.serviceCode = formValues.serviceType
        } else if (type === "material") {
            payload.items.push({
                objectCode: formValues.serviceType,
            });
            payload.serviceCode = "2";
            payload.quantity = formValues.quantity;
            payload.location = formValues.location;
        } else { // Service
            payload.items.push({
                objectCode: formValues.serviceType,
            });
            payload.serviceCode = "7";
            payload.quantity = formValues.quantity;
            payload.location = formValues.location;
        }

        if (formValues.requestor !== "") {
            payload.requestor = formValues.requestor;
        }

        if (formValues.costCentre !== "") {
            payload.costCenter = formValues.costCentre;
        }

        if (formValues.comment !== "") {
            payload.customerComment = formValues.comment;
        }

        try {
            setInProgress(true);
            await addToCart(payload).unwrap();
            setFormValues({
                serviceType: "",
                location: "",
                quantity: 1,
                requestor: "",
                costCentre: "",
                comment: "",
            });
            onSubmit();
        } catch (error) {
            const err = error as FetchBaseQueryError;

            if (err.status === 401) {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Your session has expired",
                    }
                });
            } else {
                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "general",
                        message: "Failed to save item",
                    }
                });
            }

        } finally {
            setInProgress(false);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues({
            ...formValues,
            [event.target.name]: event.target.name === "quantity"
                ? parseInt(event.target.value)
                : event.target.value,
        });
    };

    const formControlStyle = {
        margin: 1,
    };

    const inputStyle = {
        minWidth: "540px",
    }

    return (
        <Dialog
            {...other}
            onClose={handleClose}
            maxWidth="sm"
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Grid container sx={{ flex: 1, spacing: 1 }}>
                    <Grid item xs={12}>
                        <FormControl sx={ formControlStyle }>
                            <TextField
                                sx={ inputStyle }
                                variant="standard"
                                fullWidth
                                label={label}
                                name="serviceType"
                                error={formValues.serviceType === ''}
                                required
                                select
                                value={formValues.serviceType}
                                onChange={handleChange}
                            >
                                {itemTypes?.map((type) => (
                                    <MenuItem key={type.code} value={type.code}>
                                        {type.description}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </FormControl>
                    </Grid>

                    {showState &&
                        <Grid item xs={12}>
                            <FormControl sx={ formControlStyle }>
                                <TextField
                                    sx={ inputStyle }
                                    variant="standard"
                                    fullWidth
                                    label='Location'
                                    name='location'
                                    error={formValues.location === ''}
                                    required
                                    select
                                    value={formValues.location}
                                    onChange={handleChange}
                                >
                                    {locationTypes?.map((type) => (
                                        <MenuItem key={type.label} value={type.target}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </FormControl>
                        </Grid>
                    }
                    {showQuantity &&
                        <Grid item xs={12}>
                            <FormControl sx={ formControlStyle }>
                                <TextField
                                    sx={ inputStyle }
                                    variant="standard"
                                    fullWidth
                                    label="Quantity"
                                    name="quantity"
                                    type="number"
                                    error={formValues.quantity < 1}
                                    required
                                    value={formValues.quantity}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </Grid>
                    }
                    <Grid item xs={12}>
                        <FormControl sx={ formControlStyle }>
                            <TextField
                                sx={ inputStyle }
                                variant="standard"
                                fullWidth
                                label="Requestor"
                                name="requestor"
                                value={formValues.requestor}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </Grid>
                    <Grid container item xs={12}>
                        <FormControl sx={ formControlStyle }>
                            <TextField
                                sx={ inputStyle }
                                variant="standard"
                                label="Cost Centre"
                                name="costCentre"
                                value={formValues.costCentre}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </Grid>
                    <Grid container item xs={12}>
                        <FormControl sx={ formControlStyle }>
                            <TextField
                                sx={ inputStyle }
                                variant="standard"
                                multiline={true}
                                label="Comment"
                                name="comment"
                                value={formValues.comment}
                                onChange={handleChange}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                {inProgress &&
                    <CircularProgress size={20} color={"secondary"} />
                }
                <Button onClick={handleClose} disabled={inProgress} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={inProgress} color="primary" autoFocus>
                    + Add
                </Button>
            </DialogActions>
        </Dialog>
    );
}
